import type { TournamentEvent, TournamentEventBody } from './events.js';
import type { Entrant, Round, Tournament } from './types.js';

export class DomainError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DomainError';
  }
}

function renumber(players: readonly Entrant[], order: readonly string[]): Entrant[] {
  const byId = new Map(players.map((p) => [p.id, p]));
  if (order.length !== players.length || new Set(order).size !== order.length) {
    throw new DomainError('renumbering must list every player exactly once');
  }
  return order.map((id, i) => {
    const p = byId.get(id);
    if (p === undefined) throw new DomainError(`unknown player ${id}`);
    return { ...p, pairingNumber: i + 1 };
  });
}

function requirePlayer(t: Tournament, id: string): Entrant {
  const p = t.players.find((x) => x.id === id);
  if (p === undefined) throw new DomainError(`unknown player ${id}`);
  return p;
}

function replacePlayer(t: Tournament, p: Entrant): Tournament {
  return { ...t, players: t.players.map((x) => (x.id === p.id ? p : x)) };
}

function replaceRound(t: Tournament, r: Round): Tournament {
  return { ...t, rounds: t.rounds.map((x) => (x.number === r.number ? r : x)) };
}

/** Apply one event. Throws DomainError when the event does not fit the state. */
export function applyEvent(state: Tournament | null, e: TournamentEventBody): Tournament {
  if (e.type === 'TournamentCreated') {
    if (state !== null) throw new DomainError('tournament already created');
    return { id: e.id, settings: e.settings, players: [], rounds: [] };
  }
  if (state === null) throw new DomainError(`${e.type} before TournamentCreated`);
  const t = state;
  switch (e.type) {
    case 'SettingsChanged':
      return { ...t, settings: { ...t.settings, ...e.patch } };
    case 'PlayerAdded': {
      if (t.players.some((p) => p.id === e.player.id)) {
        throw new DomainError(`duplicate player id ${e.player.id}`);
      }
      const p: Entrant = { ...e.player, pairingNumber: t.players.length + 1 };
      return { ...t, players: [...t.players, p] };
    }
    case 'PlayerUpdated': {
      const p = requirePlayer(t, e.id);
      return replacePlayer(t, { ...p, ...e.patch });
    }
    case 'PlayerRemoved': {
      requirePlayer(t, e.id);
      if (t.rounds.length > 0) {
        throw new DomainError('cannot remove a player once pairing has started; withdraw instead');
      }
      const rest = t.players.filter((p) => p.id !== e.id);
      return { ...t, players: rest.map((p, i) => ({ ...p, pairingNumber: i + 1 })) };
    }
    case 'PlayersRenumbered':
      if (t.rounds.length > 0) {
        throw new DomainError('cannot renumber players once pairing has started');
      }
      return { ...t, players: renumber(t.players, e.order) };
    case 'PlayerWithdrawn': {
      const p = requirePlayer(t, e.id);
      return replacePlayer(t, { ...p, withdrawnAfterRound: e.afterRound });
    }
    case 'PlayerReinstated': {
      const p = requirePlayer(t, e.id);
      return replacePlayer(t, { ...p, withdrawnAfterRound: null });
    }
    case 'ByeRequested': {
      const p = requirePlayer(t, e.id);
      const rest = p.byeRequests.filter((b) => b.round !== e.round);
      const byeRequests = [...rest, { round: e.round, kind: e.kind }].sort(
        (a, b) => a.round - b.round,
      );
      return replacePlayer(t, { ...p, byeRequests });
    }
    case 'ByeCancelled': {
      const p = requirePlayer(t, e.id);
      return replacePlayer(t, {
        ...p,
        byeRequests: p.byeRequests.filter((b) => b.round !== e.round),
      });
    }
    case 'RoundPaired': {
      if (e.round !== t.rounds.length + 1) {
        throw new DomainError(
          `round ${e.round} paired out of order (next is ${t.rounds.length + 1})`,
        );
      }
      const seen = new Set<string>();
      for (const g of e.games) {
        for (const id of [g.white, g.black]) {
          requirePlayer(t, id);
          if (seen.has(id)) throw new DomainError(`player ${id} appears twice in round ${e.round}`);
          seen.add(id);
        }
      }
      for (const b of e.byes) {
        requirePlayer(t, b.player);
        if (seen.has(b.player)) {
          throw new DomainError(`player ${b.player} appears twice in round ${e.round}`);
        }
        seen.add(b.player);
      }
      const round: Round = { number: e.round, games: e.games, byes: e.byes, published: false };
      return { ...t, rounds: [...t.rounds, round] };
    }
    case 'RoundUnpaired': {
      if (e.round !== t.rounds.length || e.round < 1) {
        throw new DomainError('only the last round can be unpaired');
      }
      return { ...t, rounds: t.rounds.slice(0, -1) };
    }
    case 'RoundEdited': {
      const r = t.rounds.find((x) => x.number === e.round);
      if (r === undefined) throw new DomainError(`round ${e.round} is not paired`);
      if (r.games.some((g) => g.result.white !== '*' || g.result.black !== '*')) {
        throw new DomainError(
          `round ${e.round} already has results; clear them before editing the pairing`,
        );
      }
      const seen = new Set<string>();
      for (const id of [
        ...e.games.flatMap((g) => [g.white, g.black]),
        ...e.byes.map((b) => b.player),
      ]) {
        requirePlayer(t, id);
        if (seen.has(id)) throw new DomainError(`player ${id} appears twice in round ${e.round}`);
        seen.add(id);
      }
      return replaceRound(t, { ...r, games: e.games, byes: e.byes });
    }
    case 'ResultEntered': {
      const r = t.rounds.find((x) => x.number === e.round);
      if (r === undefined) throw new DomainError(`round ${e.round} is not paired`);
      if (!r.games.some((g) => g.board === e.board)) {
        throw new DomainError(`round ${e.round} has no board ${e.board}`);
      }
      return replaceRound(t, {
        ...r,
        games: r.games.map((g) => (g.board === e.board ? { ...g, result: e.result } : g)),
      });
    }
    case 'RoundPublished': {
      const r = t.rounds.find((x) => x.number === e.round);
      if (r === undefined) throw new DomainError(`round ${e.round} is not paired`);
      return replaceRound(t, { ...r, published: true });
    }
  }
}

/** Fold a whole event log into a tournament. */
export function replay(events: readonly (TournamentEvent | TournamentEventBody)[]): Tournament {
  let state: Tournament | null = null;
  for (const e of events) state = applyEvent(state, e);
  if (state === null) throw new DomainError('empty event log');
  return state;
}
